import { api } from "./restUtil";
import { defineApiMutation, defineApiQuery } from "utils/queries";
import { LocalStorageKeys } from "utils/storage";
const PREVIOUS_SEARCH_NUMBER_OF_ITEMS = 5;

export type DictionarySavedWord = {
  id: string;
  word: string;
  pos: string | null;
  notes: string | null;
  definition: string | null;
  translation: string | null;
  created: string | null;
  updated: string | null;
  audio: string | null;
};

export type DictionaryMeaning = {
  definition: string;
  examples: string[];
  domain: string | null;
  pronunciation: {
    audio: string | null;
    phonetic: string | null;
  };
};

type DictionaryPos = Record<string, DictionaryMeaning[]>;

type DictionarySearchParams = {
  text: string;
};

export type DictionarySearchResponse = {
  word: string;
  provider: string;
  pos: DictionaryPos;
};

export const dictionarySearchMutation = defineApiMutation<
  DictionarySearchResponse,
  DictionarySearchParams
>({
  path: "/dictionary/search",
  mutationFn: (path, params) =>
    api.get<DictionarySearchResponse, DictionarySearchParams>(path, params),
});

type GetPersonalDictionaryParams = {
  page?: number;
  size?: number;
  order_by?: "created" | "updated" | null;
  sort?: "asc" | "desc";
  filter?: string | null;
};

export type GetPersonalDictionaryResponse = {
  list: DictionarySavedWord[];
  total_count: number;
};

export const personalDictionaryQuery = defineApiQuery<
  GetPersonalDictionaryResponse,
  GetPersonalDictionaryParams
>({
  key: "/dictionary/saved",
});

type AddWordToDictionaryPayload = {
  word: string;
  pos?: string;
  notes?: string;
  definition?: string;
  translation?: string;
};

export function addWordToDictionary(data: AddWordToDictionaryPayload) {
  return api.post<AddWordToDictionaryPayload, DictionarySavedWord>(
    "/dictionary/saved",
    data
  );
}

export const addSavedWordMutation = defineApiMutation<
  DictionarySavedWord,
  AddWordToDictionaryPayload
>({
  path: "/dictionary/saved",
  mutationFn: (_path, payload) => addWordToDictionary(payload),
});

type UpdateDictionaryPayload = {
  pos?: string;
  notes?: string;
  definition?: string;
  translation?: string;
};

export function updateDictionary(id: string, data: UpdateDictionaryPayload) {
  return api.patch<UpdateDictionaryPayload, DictionarySavedWord>(
    `/dictionary/saved/${id}`,
    data
  );
}

type UpdateSavedWordVariables = {
  id: string;
  payload: UpdateDictionaryPayload;
};

export const updateSavedWordMutation = defineApiMutation<
  DictionarySavedWord,
  UpdateSavedWordVariables
>({
  path: ({ id }) => `/dictionary/saved/${id}`,
  mutationFn: (_path, variables) => updateDictionary(variables.id, variables.payload),
});

type DictionaryExamplesParams = {
  id: string;
  count?: 1 | 2 | 3 | 4 | 5;
};

export type DictionaryExamplesResponse =
  | {
      noContent: true;
      body: {
        list?: string[];
      };
    }
  | {
      noContent: false;
      body: {
        list: string[];
      };
    };

export async function getDictionaryExamples(
  data: DictionaryExamplesParams
): Promise<DictionaryExamplesResponse> {
  // rawResponse to distinguish 204 (examples still generating) from a 200 with data.
  const resp = await api.get<Response, DictionaryExamplesParams>(
    "/dictionary/examples",
    data,
    { rawResponse: true }
  );
  if (resp.status === 204) {
    return { noContent: true, body: {} };
  }
  return { noContent: false, body: await resp.json() };
}

export function deleteWordFromDictionary(id: string) {
  return api.delete(`/dictionary/saved/${id}`);
}

export const deleteSavedWordMutation = defineApiMutation<void, { id: string }>({
  path: ({ id }) => `/dictionary/saved/${id}`,
  mutationFn: (_path, { id }) => deleteWordFromDictionary(id),
});

export function addItemToPreviousSearch(item: string) {
  let previousSearch = getPreviousSearch();

  if (previousSearch.includes(item)) {
    return previousSearch;
  }

  previousSearch = [...previousSearch, item];
  if (previousSearch.length > PREVIOUS_SEARCH_NUMBER_OF_ITEMS) {
    previousSearch = previousSearch.slice(-PREVIOUS_SEARCH_NUMBER_OF_ITEMS);
  }

  localStorage.setItem(
    LocalStorageKeys.dictionaryPreviousSearch,
    JSON.stringify(previousSearch)
  );
  return previousSearch;
}

export function getPreviousSearch(): string[] {
  const previousSearch = localStorage.getItem(LocalStorageKeys.dictionaryPreviousSearch);
  if (!previousSearch) {
    return [];
  }

  const parsed = JSON.parse(previousSearch) as unknown;
  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter((item): item is string => typeof item === "string");
}
